/**
 * Onboarding endpoints (architecture §6): CV upload, the streamed interview,
 * finalize, and the profile read/edit/refine calls. The backend routes
 * (PIVOT-041, a later wave) are not wired yet; these functions already
 * target the documented path so wiring them up later is a no-op here.
 */

import { ApiError, httpClient } from './client'
import { streamSse, type SseEvent } from './sse'
import type {
  CandidateEvidence,
  CareerBaseline,
  CareerBaselineCorrection,
  ConstraintAnswers,
  MatchSetupAnswers,
  OnboardingContract,
  OnboardingIntent,
  PolicyDraft,
  PolicyReviewResponse,
  Profile,
  RuleStrength,
  Rubric,
  RubricField,
  SearchPolicyContent,
  SearchPolicyVersion,
  UserProfile,
} from '../types/domain'

export interface ConsentResponse {
  id: string
  user_id: string
  policy_version: string
  signed_at: string
}

/** Records consent to the current privacy-policy version before any CV upload. */
export function recordConsent(): Promise<ConsentResponse> {
  return httpClient.post<ConsentResponse>('/onboarding/consent')
}

/**
 * Clears an abandoned onboarding draft before a deliberate redo.
 * The saved profile and saved CV remain available.
 */
export function restartOnboarding(signal?: AbortSignal): Promise<void> {
  return httpClient.post<void>('/onboarding/restart', undefined, signal)
}

export interface CvUploadResponse {
  cv_ref: string
}

export function uploadCv(file: File): Promise<CvUploadResponse> {
  const form = new FormData()
  form.append('file', file)
  return httpClient.upload<CvUploadResponse>('/onboarding/cv', form)
}

/** A CV the candidate already uploaded — `GET /api/onboarding/cv` (PIVOT-085). */
export interface SavedCv {
  filename: string
  /** ISO 8601, as the backend serializes `datetime`. */
  uploaded_at: string
  /** The stored parse's summary; proof we still have the analysis, not just the file. */
  analysis_summary: string
}

/**
 * The CV on file for this user, or `null` when there is none.
 *
 * The backend 404s when nothing is stored (same convention as `GET /profile`),
 * which here is an ordinary answer rather than a failure — "no CV yet" is what
 * a brand-new candidate looks like. Every other status still rejects.
 */
export async function getSavedCv(signal?: AbortSignal): Promise<SavedCv | null> {
  try {
    return await httpClient.get<SavedCv>('/onboarding/cv', signal)
  } catch (reason) {
    if (reason instanceof ApiError && reason.status === 404) return null
    throw reason
  }
}

/**
 * Streams one interview turn — `POST /api/onboarding/interview` (PIVOT-083).
 *
 * `message` is the candidate's reply. Pass `null` to open the interview; once
 * it is open, `null` asks the server to replay the turn the candidate was last
 * shown, which is how a page reload resumes instead of restarting. A blank
 * string is rejected with a 422 — it is neither a reply nor an open.
 *
 * The reply goes in the body, not the query string: request logs record full
 * URLs, and what someone says about their career is not log material
 * (constitution §V).
 *
 * `intent` (the PIVOT-075 "why are you here" choice) only matters on the call
 * that opens the interview, which is where it shapes the system prompt
 * (PIVOT-076); later turns use the intent the session was opened with.
 */
export function streamInterview(
  message: string | null = null,
  intent?: OnboardingIntent,
  signal?: AbortSignal,
): AsyncGenerator<SseEvent> {
  return streamSse('/onboarding/interview', {
    method: 'POST',
    body: JSON.stringify(intent ? { message, intent } : { message }),
    signal,
  })
}

/**
 * What `GET /api/onboarding/constraints` reports (PIVOT-232).
 *
 * `interview_done` is carried from the server rather than guessed here: the
 * entry resolver needs "is the constraints step this candidate's next step",
 * and saved `answers` cannot answer that — a candidate who has answered
 * nothing yet still belongs on the step.
 */
export interface SavedConstraints {
  interview_done: boolean
  answers: ConstraintAnswers | null
}

/**
 * The constraints step's answers on file, plus whether the interview is done.
 *
 * A READ-ONLY probe, deliberately, and the reason the step has one at all: the
 * interview has none (`streamInterview(null)` *opens* an interview rather than
 * reporting that none exists), so `Onboarding`'s entry resolver could never
 * ask where a candidate was without paying for a model call. This costs
 * nothing and is safe to call on every mount.
 */
export function getConstraints(signal?: AbortSignal): Promise<SavedConstraints> {
  return httpClient.get<SavedConstraints>('/onboarding/constraints', signal)
}

/**
 * Records the constraints step — `PUT /api/onboarding/constraints` (PIVOT-232).
 *
 * Writes to the onboarding draft, not the profile: the profile does not exist
 * until finalize, which reads these back and applies them over the profile the
 * model built. The server rejects (422) a body that leaves a constraint
 * without either a value or its explicit no-constraint marker.
 */
export function saveConstraints(answers: ConstraintAnswers): Promise<ConstraintAnswers> {
  return httpClient.put<ConstraintAnswers>('/onboarding/constraints', answers)
}

/**
 * The four reloadable artifacts finalize builds or restores (PIVOT-299,
 * R10-A1). Used to omit `candidate_evidence`/`policy_draft` entirely — a real
 * drift from the backend's `FinalizeOnboardingResponse`, invisible until
 * something needed one of them, exactly the kind of silent disagreement
 * CLAUDE.md's "mirror wire shapes by hand" rule exists to catch.
 */
export interface FinalizeOnboardingResponse {
  profile: UserProfile
  rubric: Rubric
  candidate_evidence: CandidateEvidence
  policy_draft: PolicyDraft
  /** The baseline finalize proposed (PIVOT-367). The review step needs it,
   * and the evidence above it, to show its baseline card at all. `null` is an
   * account with nothing on file — its own review state, never a blank job. */
  career_baseline: CareerBaseline | null
}

export function finalizeOnboarding(
  answers: Record<string, string>,
  targetContract?: OnboardingContract,
): Promise<FinalizeOnboardingResponse> {
  return httpClient.post<FinalizeOnboardingResponse>('/onboarding/finalize', {
    answers,
    ...(targetContract ? { target_contract: targetContract } : {}),
  })
}

export function getProfile(signal?: AbortSignal): Promise<Profile> {
  return httpClient.get<Profile>('/profile', signal)
}

/**
 * Whether finalize has already run for this candidate — `true` once a
 * profile row exists, regardless of `profile_complete` (v2 only flips that
 * once the policy is approved too). The entry resolver uses this to route a
 * v2 candidate who finalized but left before approving straight back to the
 * policy-review step, instead of restarting an interview whose draft
 * finalize already cleared (PIVOT-299, R10-A1).
 */
export async function hasSavedProfile(signal?: AbortSignal): Promise<boolean> {
  try {
    await getProfile(signal)
    return true
  } catch (reason) {
    if (reason instanceof ApiError && reason.status === 404) return false
    throw reason
  }
}

/**
 * `rubric` is optional and used only by the Refine chat's per-field undo
 * (PIVOT-109) — the ordinary profile-edit save (`ProfileReview`) never passes
 * it, since editing `UserProfile` fields does not touch the rubric.
 *
 * The backend route (`PUT /profile`, `api/routes/onboarding.py::
 * update_profile`) accepts this as `ProfileUpdateRequest` — `UserProfile`
 * plus an optional `rubric` — and forwards it to `apply_edits`, which
 * recomputes `Rubric.version` itself rather than trusting whatever this
 * call sends (same rule `RefineService.refine` follows for a chat-driven
 * save). This used to be a real gap: the route's body was bare `UserProfile`
 * with no slot for a rubric at all, so one sent here was silently dropped
 * (pydantic's default `extra="ignore"`) and undo would have flipped a row to
 * "restored" over a save that never happened.
 */
export function updateProfile(
  profile: UserProfile,
  rubric?: Rubric,
  careerBaseline?: CareerBaselineCorrection,
): Promise<Profile> {
  // PIVOT-367: `career_baseline` is omitted unless the person actually
  // confirmed or corrected it. Sending it on every save would re-confirm a
  // baseline nobody looked at, and the server would then have to guess which
  // saves were career statements and which were not.
  return httpClient.put<Profile>('/profile', {
    ...profile,
    ...(rubric ? { rubric } : {}),
    ...(careerBaseline ? { career_baseline: careerBaseline } : {}),
  })
}

/** One turn of the refine conversation, as the client replays it to the server. */
export interface RefineTurn {
  role: 'user' | 'assistant'
  content: string
}

/**
 * What `POST /profile/refine` did (backend `RefineResult`).
 *
 * `changed` is diffed server-side from the stored rubric, so it reports what
 * the turn actually moved rather than what the model says it moved. An empty
 * `changed` means nothing was saved — the adviser asked a question instead of
 * guessing — and `rubric` is then the unchanged one.
 */
export interface RefineResponse {
  reply: string
  changed: RubricField[]
  rubric: Rubric
}

/**
 * One refine turn — `POST /api/profile/refine`.
 *
 * Not SSE, unlike the interview: the server makes one structured-output call,
 * so the rubric arrives whole and there is nothing to stream. `history` is the
 * conversation so far; the server bounds it at 12 turns and treats it as
 * context only, re-reading the authoritative rubric from the row each time.
 */
export function refineProfile(
  message: string,
  history: RefineTurn[],
  signal?: AbortSignal,
): Promise<RefineResponse> {
  return httpClient.post<RefineResponse>('/profile/refine', { message, history }, signal)
}

// --------------------------------------------------------------------------- #
// The match-setup form and policy review (PIVOT-299)
// --------------------------------------------------------------------------- #

/**
 * What `GET /api/onboarding/match-setup` reports.
 *
 * `onboarding_contract` is the fact the entry resolver needs that
 * `getConstraints` does not carry: which of the two forms — this one, or the
 * legacy `ConstraintsStep` — a candidate belongs on. `answers` stays `null`
 * until the form is submitted, same convention as `SavedConstraints`.
 */
export interface SavedMatchSetup {
  /** Distinguishes a reload during an interview from a rebuild not yet started. */
  interview_started: boolean
  interview_done: boolean
  onboarding_contract: OnboardingContract
  answers: MatchSetupAnswers | null
}

/**
 * The match-setup answers on file, the contract, and the interview state —
 * `GET /api/onboarding/match-setup`. Read-only and free, like `getConstraints`;
 * safe to call on every mount.
 */
export function getMatchSetup(signal?: AbortSignal): Promise<SavedMatchSetup> {
  return httpClient.get<SavedMatchSetup>('/onboarding/match-setup', signal)
}

/**
 * Records the match-setup form — `PUT /api/onboarding/match-setup`. Writes to
 * the policy draft, not the profile: a v2 candidate's location/contract/pay
 * answers become policy rules, never the legacy `UserProfile.locations` field
 * (R10-A7). The server rejects (422) a body that leaves a hard question
 * without either a value or its no-preference marker.
 */
export function saveMatchSetup(answers: MatchSetupAnswers): Promise<MatchSetupAnswers> {
  return httpClient.put<MatchSetupAnswers>('/onboarding/match-setup', answers)
}

/**
 * The exact policy ready to display and approve — `GET
 * /api/onboarding/policy/review`. `content`, `policy_fingerprint`, and
 * `active_policy_version_id` are computed together, once, on the server; hand
 * them to `approvePolicy` completely unchanged (never re-derive the
 * fingerprint here) — see the type's own docstring in `types/domain.ts`.
 */
export function getPolicyReview(signal?: AbortSignal): Promise<PolicyReviewResponse> {
  return httpClient.get<PolicyReviewResponse>('/onboarding/policy/review', signal)
}

/**
 * Approves ONE employer refusal's exact wording —
 * `POST /api/onboarding/policy/employer-exclusion/approve` (PIVOT-370).
 *
 * `displayedMeaning` is the token `getPolicyReview` handed back for this
 * rule, echoed unchanged: the server recomputes it for the chosen strength
 * and refuses a mismatch, so the sentence on screen and the rule that binds
 * are the same thing (constitution §V). `strength` is the person's own hard
 * (remove the job) or soft (rank it down) choice.
 *
 * The server re-stamps the rule, so its `rule_id` changes — reload the
 * review afterwards rather than addressing it by the old id.
 * `ApiError.message` carries `employer_exclusion_meaning_mismatch` (400) or
 * `employer_exclusion_not_found` (404 — a stale screen).
 */
export function approveEmployerExclusion(
  ruleId: string,
  displayedMeaning: string,
  strength: RuleStrength,
): Promise<SearchPolicyContent> {
  return httpClient.post<SearchPolicyContent>(
    '/onboarding/policy/employer-exclusion/approve',
    { rule_id: ruleId, displayed_meaning: displayedMeaning, strength },
  )
}

/**
 * Approves exactly the policy a `getPolicyReview` call displayed —
 * `POST /api/onboarding/policy/approve`. `ApiError.message` carries the
 * server's reason on a refusal: `policy_action_required` (an unanswered
 * category), `policy_fingerprint_mismatch`/`policy_rule_invalid` (the posted
 * content does not match its own fingerprint), `policy_changed` (409 — the
 * active policy moved since this content was displayed, e.g. a second tab
 * approved first), or `candidate_evidence_required`/`policy_evidence_invalid`.
 *
 * `careerBaseline` (PIVOT-367, A4) is the confirmation the person made on the
 * profile-review screen one step earlier. It rides on the approval because
 * the two commit together: the server writes the baseline inside the same
 * transaction as the policy version, so a failed approval leaves no confirmed
 * career fact behind. An approval that carries none changes no career fact.
 * The language list follows the same rule: it is the exact list shown in this
 * review, and the server writes it inside the same approval transaction.
 */
export function approvePolicy(
  review: PolicyReviewResponse,
  careerBaseline?: CareerBaselineCorrection,
): Promise<SearchPolicyVersion> {
  return httpClient.post<SearchPolicyVersion>('/onboarding/policy/approve', {
    content: review.content,
    policy_fingerprint: review.policy_fingerprint,
    displayed_active_policy_version_id: review.active_policy_version_id,
    displayed_language_evidence_sha: review.displayed_language_evidence_sha,
    displayed_language_approval_revision: review.displayed_language_approval_revision,
    language_answers: review.language_answers,
    ...(careerBaseline ? { career_baseline: careerBaseline } : {}),
  })
}
